import { submissionService } from '@oneblink/apps-react'
import { useState, useMemo, useCallback } from 'react'
import { useSystemMaintenance } from '../providers/SystemMaintenanceProvider'

interface FetchingMaintenanceWindowState {
  isFetchingMaintenanceWindow: boolean
  fetchMaintenanceWindowError?: string
}

export default function useCheckMaintenanceOnSubmit(
  submitFn: (
    newFormSubmission: submissionService.NewFormSubmission,
  ) => Promise<void> | void,
): [
  FetchingMaintenanceWindowState,
  {
    wrappedSubmitFn: (
      newFormSubmission: submissionService.NewFormSubmission,
    ) => Promise<void> | void
    clearMaintenanceMessage: () => void
  },
] {
  const { checkMaintenanceWindow } = useSystemMaintenance()
  const [fetchingMaintenanceWindowState, setFetchingMaintenanceWindowState] =
    useState<FetchingMaintenanceWindowState>({
      isFetchingMaintenanceWindow: false,
    })

  const clearMaintenanceMessage = useCallback(() => {
    setFetchingMaintenanceWindowState({ isFetchingMaintenanceWindow: false })
  }, [])

  const wrappedSubmitFn = useMemo(() => {
    if (!checkMaintenanceWindow) {
      return submitFn
    }

    return async (newFormSubmission: submissionService.NewFormSubmission) => {
      try {
        setFetchingMaintenanceWindowState({ isFetchingMaintenanceWindow: true })
        const { inMaintenanceWindow, message } = await checkMaintenanceWindow()
        if (inMaintenanceWindow) {
          throw new Error(message)
        }
        return await submitFn(newFormSubmission)
      } catch (e) {
        console.error('error while checking maintenance window:', e)
        setFetchingMaintenanceWindowState({
          isFetchingMaintenanceWindow: false,
          fetchMaintenanceWindowError:
            e instanceof Error
              ? e.message
              : 'The system is currently unavailable, please try again later',
        })
        throw e
      } finally {
        setFetchingMaintenanceWindowState((state) => ({
          ...state,
          isFetchingMaintenanceWindow: false,
        }))
      }
    }
  }, [checkMaintenanceWindow, submitFn])

  return [
    fetchingMaintenanceWindowState,
    { clearMaintenanceMessage, wrappedSubmitFn },
  ]
}
